import React, { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BRAND_BLUE = '#0B3D6E';
const BRAND_TEAL = '#0F6E56';

const STATUS_CONFIG = {
  pending:    { label:'En attente',  labelEn:'Pending',    color:'#d97706', bg:'#fef3c7', step:1 },
  confirmed:  { label:'Confirmée',   labelEn:'Confirmed',  color:'#0B3D6E', bg:'#E6F1FB', step:2 },
  processing: { label:'En cours',    labelEn:'Processing', color:'#7C3AED', bg:'#EDE9FE', step:3 },
  delivered:  { label:'Livrée',      labelEn:'Delivered',  color:'#0F6E56', bg:'#E1F5EE', step:4 },
  cancelled:  { label:'Annulée',     labelEn:'Cancelled',  color:'#dc2626', bg:'#fee2e2', step:0 },
};

const STEPS = [
  { key:'pending',    icon:'📋', label:'Reçue',       labelEn:'Received' },
  { key:'confirmed',  icon:'✅', label:'Confirmée',   labelEn:'Confirmed' },
  { key:'processing', icon:'📦', label:'En préparation', labelEn:'Processing' },
  { key:'delivered',  icon:'🚚', label:'Livrée',      labelEn:'Delivered' },
];

export default function MesCommandes({ lang }) {
  const fr = lang === 'fr';
  const [orderNum, setOrderNum] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!orderNum.trim()) return;
    setLoading(true);
    setError('');
    setOrder(null);
    try {
      const { data } = await axios.get(`${API}/orders/track/${orderNum.trim().toUpperCase()}`);
      setOrder(data.data);
    } catch (err) {
      setError(err.response?.data?.message || (fr ? 'Commande introuvable. Vérifiez votre numéro.' : 'Order not found. Please check your number.'));
    } finally {
      setLoading(false);
    }
  };

  const sc = order ? (STATUS_CONFIG[order.status] || STATUS_CONFIG.pending) : null;
  const currentStep = sc?.step || 0;
  const isCancelled = order?.status === 'cancelled';

  return (
    <div style={{ maxWidth:680, margin:'0 auto', padding:'2rem 1rem' }}>

      {/* Header */}
      <div style={{ marginBottom:'2rem' }}>
        <div style={{ fontSize:11, fontWeight:500, textTransform:'uppercase', letterSpacing:'1px', color:BRAND_TEAL, marginBottom:6 }}>
          {fr ? 'Espace client' : 'Client portal'}
        </div>
        <h1 style={{ fontSize:22, fontWeight:500, marginBottom:8 }}>
          {fr ? 'Suivre ma commande' : 'Track my order'}
        </h1>
        <p style={{ fontSize:13, color:'#64748b', lineHeight:1.7 }}>
          {fr ? 'Entrez votre numéro de commande reçu par email ou sur votre confirmation pour suivre l\'état de votre livraison.'
               : 'Enter the order number received by email or on your confirmation to track the status of your delivery.'}
        </p>
      </div>

      {/* Search form */}
      <form onSubmit={handleSearch} style={{ background:'#fff', border:'0.5px solid #e2e8f0', borderRadius:14, padding:'1.5rem', marginBottom:'1.5rem', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
        <label style={{ display:'block', fontSize:12, fontWeight:500, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:8 }}>
          {fr ? 'Numéro de commande' : 'Order number'}
        </label>
        <div style={{ display:'flex', gap:10 }}>
          <input
            type="text"
            value={orderNum}
            onChange={e => setOrderNum(e.target.value)}
            placeholder="CMD-2025-0001"
            style={{ flex:1, border:'0.5px solid #e2e8f0', borderRadius:8, padding:'11px 14px', fontSize:14, fontFamily:'monospace', outline:'none', background:'#f8fafc', letterSpacing:'1px' }}
            onFocus={e => e.target.style.borderColor = BRAND_BLUE}
            onBlur={e => e.target.style.borderColor = '#e2e8f0'}
          />
          <button type="submit" disabled={loading || !orderNum.trim()} style={{
            background: BRAND_BLUE, color:'#fff', border:'none', padding:'11px 22px',
            borderRadius:8, fontSize:13, fontWeight:500, cursor:'pointer', whiteSpace:'nowrap',
            opacity: (!orderNum.trim() || loading) ? 0.6 : 1
          }}>
            {loading ? '...' : (fr ? 'Rechercher' : 'Search')}
          </button>
        </div>
        {error && (
          <div style={{ marginTop:10, background:'#fee2e2', border:'0.5px solid #fca5a5', borderRadius:8, padding:'10px 12px', fontSize:13, color:'#dc2626', display:'flex', alignItems:'center', gap:8 }}>
            <svg viewBox="0 0 24 24" style={{ width:16, height:16, fill:'none', stroke:'#dc2626', strokeWidth:2, flexShrink:0 }}>
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {error}
          </div>
        )}
      </form>

      {/* Order result */}
      {order && (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

          {/* Status header */}
          <div style={{ background:sc.bg, border:`0.5px solid ${sc.color}30`, borderRadius:14, padding:'1.5rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12, flexWrap:'wrap', gap:8 }}>
              <div>
                <div style={{ fontSize:11, color:sc.color, fontWeight:500, textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:4 }}>
                  {fr ? 'Statut de la commande' : 'Order status'}
                </div>
                <div style={{ fontSize:20, fontWeight:500, color:sc.color }}>
                  {fr ? sc.label : sc.labelEn}
                </div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:10, color:'#64748b', marginBottom:2 }}>{fr ? 'N° commande' : 'Order no.'}</div>
                <div style={{ fontSize:16, fontWeight:500, fontFamily:'monospace', color:BRAND_BLUE }}>{order.orderNumber}</div>
              </div>
            </div>

            {/* Progress steps */}
            {!isCancelled && (
              <div style={{ display:'flex', alignItems:'center', gap:0, marginTop:16 }}>
                {STEPS.map((step, idx) => {
                  const done = currentStep > idx;
                  const active = currentStep === idx + 1;
                  return (
                    <React.Fragment key={step.key}>
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flex:1 }}>
                        <div style={{
                          width:36, height:36, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16,
                          background: done || active ? sc.color : '#e2e8f0',
                          border: `2px solid ${done || active ? sc.color : '#e2e8f0'}`,
                          marginBottom:6, transition:'all 0.3s'
                        }}>
                          {done ? (
                            <svg viewBox="0 0 24 24" style={{ width:16, height:16, fill:'none', stroke:'#fff', strokeWidth:2.5 }}>
                              <path d="M5 13l4 4L19 7"/>
                            </svg>
                          ) : (
                            <span style={{ filter: done || active ? 'none' : 'grayscale(1)', fontSize:14 }}>{step.icon}</span>
                          )}
                        </div>
                        <div style={{ fontSize:10, fontWeight: active ? 500 : 400, color: active ? sc.color : '#94a3b8', textAlign:'center', lineHeight:1.3 }}>
                          {fr ? step.label : step.labelEn}
                        </div>
                      </div>
                      {idx < STEPS.length - 1 && (
                        <div style={{ height:2, flex:1, background: currentStep > idx + 1 ? sc.color : '#e2e8f0', marginBottom:22, transition:'all 0.3s' }}/>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            {isCancelled && (
              <div style={{ marginTop:12, background:'rgba(220,38,38,0.08)', borderRadius:8, padding:'10px 12px', fontSize:13, color:'#dc2626' }}>
                {fr ? 'Cette commande a été annulée. Contactez-nous pour plus d\'informations.' : 'This order has been cancelled. Contact us for more information.'}
              </div>
            )}
          </div>

          {/* Order details */}
          <div style={{ background:'#fff', border:'0.5px solid #e2e8f0', borderRadius:14, padding:'1.5rem', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
            <h2 style={{ fontSize:14, fontWeight:500, marginBottom:14, paddingBottom:10, borderBottom:'0.5px solid #f1f5f9' }}>
              {fr ? 'Détails de la commande' : 'Order details'}
            </h2>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }} className="order-detail-grid">
              <style>{`@media(max-width:480px){.order-detail-grid{grid-template-columns:1fr !important}}`}</style>
              {[
                [fr?'Établissement':'Institution', order.institution],
                [fr?'Ville':'City', order.city || '—'],
                [fr?'Responsable':'Contact', order.contactName],
                [fr?'Téléphone':'Phone', order.phone],
                [fr?'Mode de paiement':'Payment', order.paymentMethod || '—'],
                [fr?'Date de commande':'Order date', new Date(order.createdAt).toLocaleDateString(fr?'fr-FR':'en-GB',{day:'2-digit',month:'long',year:'numeric'})],
                ...(order.deliveryDate ? [[fr?'Livraison souhaitée':'Desired delivery', new Date(order.deliveryDate).toLocaleDateString(fr?'fr-FR':'en-GB')]] : []),
              ].map(([l,v]) => (
                <div key={l}>
                  <div style={{ fontSize:11, color:'#94a3b8', marginBottom:2 }}>{l}</div>
                  <div style={{ fontSize:13, fontWeight:500 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Products */}
          {(order.freeText || order.items?.length > 0) && (
            <div style={{ background:'#fff', border:'0.5px solid #e2e8f0', borderRadius:14, padding:'1.5rem', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
              <h2 style={{ fontSize:14, fontWeight:500, marginBottom:14, paddingBottom:10, borderBottom:'0.5px solid #f1f5f9' }}>
                {fr ? 'Produits commandés' : 'Ordered products'}
              </h2>
              {order.freeText && order.freeText.split('\n').filter(Boolean).map((line, i) => (
                <div key={i} style={{ background:'#f8fafc', borderRadius:8, padding:'9px 12px', fontSize:13, marginBottom:6 }}>{line}</div>
              ))}
              {(order.items || []).map((item, i) => (
                <div key={i} style={{ background:'#f8fafc', borderRadius:8, padding:'9px 12px', fontSize:13, marginBottom:6, display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:4 }}>
                  <span>{item.productName} <span style={{ color:'#94a3b8', fontSize:11 }}>({item.productRef})</span></span>
                  <span style={{ fontWeight:500 }}>×{item.quantity} — {item.unitPrice}</span>
                </div>
              ))}
            </div>
          )}

          {/* Notes */}
          {order.notes && (
            <div style={{ background:'#f8fafc', border:'0.5px solid #e2e8f0', borderRadius:12, padding:'1rem 1.2rem' }}>
              <div style={{ fontSize:11, color:'#94a3b8', marginBottom:4 }}>{fr?'Remarques':'Notes'}</div>
              <div style={{ fontSize:13 }}>{order.notes}</div>
            </div>
          )}

          {/* Help */}
          <div style={{ background:`linear-gradient(135deg,${BRAND_BLUE}08,${BRAND_TEAL}08)`, border:`0.5px solid ${BRAND_TEAL}30`, borderRadius:12, padding:'1.2rem', display:'flex', gap:12, alignItems:'flex-start' }}>
            <div style={{ width:36, height:36, background:BRAND_TEAL, borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg viewBox="0 0 24 24" style={{ width:18, height:18, fill:'none', stroke:'#fff', strokeWidth:2 }}>
                <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize:13, fontWeight:500, color:BRAND_TEAL, marginBottom:4 }}>
                {fr ? 'Besoin d\'aide ?' : 'Need help?'}
              </div>
              <div style={{ fontSize:12, color:'#64748b', lineHeight:1.65 }}>
                {fr ? 'Contactez notre équipe commerciale au +212 5XX XX XX XX ou par email à contact@lomedmedicare.ma (Lun–Sam 8h–18h).'
                     : 'Contact our sales team at +212 5XX XX XX XX or by email at contact@lomedmedicare.ma (Mon–Sat 8am–6pm).'}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            <button onClick={() => { setOrder(null); setOrderNum(''); }} style={{
              flex:1, border:'0.5px solid #e2e8f0', background:'#fff', color:'#64748b',
              padding:'11px', borderRadius:8, fontSize:13, cursor:'pointer'
            }}>
              {fr ? 'Nouvelle recherche' : 'New search'}
            </button>
            <a href="/reservation" style={{
              flex:1, background:BRAND_BLUE, color:'#fff', padding:'11px', borderRadius:8,
              fontSize:13, fontWeight:500, textAlign:'center', textDecoration:'none', display:'flex', alignItems:'center', justifyContent:'center'
            }}>
              {fr ? 'Nouvelle commande' : 'New order'}
            </a>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!order && !error && !loading && (
        <div style={{ background:'#fff', border:'0.5px solid #e2e8f0', borderRadius:14, padding:'2.5rem', textAlign:'center', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
          <svg viewBox="0 0 24 24" style={{ width:44, height:44, fill:'none', stroke:'#cbd5e1', strokeWidth:1.5, margin:'0 auto 14px', display:'block' }}>
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
            <path d="M9 12h6M9 16h4"/>
          </svg>
          <div style={{ fontSize:14, fontWeight:500, color:'#94a3b8', marginBottom:6 }}>
            {fr ? 'Entrez votre numéro de commande' : 'Enter your order number'}
          </div>
          <div style={{ fontSize:12, color:'#cbd5e1' }}>
            {fr ? 'Format : CMD-2025-XXXX' : 'Format: CMD-2025-XXXX'}
          </div>
        </div>
      )}
    </div>
  );
}

